import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const isIos = () => {
  if (typeof window === "undefined") return false;
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.navigator.standalone;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

const STORAGE_DISMISS_KEY = "fsk-install-dismissed";

const InstallAppButton = () => {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && localStorage.getItem(STORAGE_DISMISS_KEY) === "1"
  );

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    if (isIos() && !dismissed) {
      setShowIosHint(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [dismissed]);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      setInstalled(true);
    }
    setDeferred(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowIosHint(false);
    localStorage.setItem(STORAGE_DISMISS_KEY, "1");
  };

  if (installed) return null;

  // Android / desktop Chrome — show a clean install button when beforeinstallprompt fires
  if (deferred) {
    return (
      <div
        className="bg-[#1D3557] text-white rounded-xl p-3 mb-4 flex items-center gap-3 shadow-md"
        data-testid="install-app-banner"
      >
        <Download className="w-5 h-5 text-[#E9C46A] flex-shrink-0" />
        <div className="flex-1">
          <p className="font-bold text-sm" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Install FarmSmart Admin
          </p>
          <p className="text-xs text-white/80">One tap from your phone home screen</p>
        </div>
        <button
          onClick={handleInstall}
          className="bg-[#E63946] hover:bg-[#E9C46A] hover:text-[#1D3557] text-white font-bold text-sm px-4 py-2 rounded-lg transition-colors"
          data-testid="install-app-btn"
        >
          Install
        </button>
        <button
          onClick={handleDismiss}
          className="text-white/60 hover:text-white"
          aria-label="Dismiss"
          data-testid="install-app-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // iOS Safari — no install prompt available, show Add-to-Home-Screen instructions
  if (showIosHint) {
    return (
      <div
        className="bg-[#1D3557] text-white rounded-xl p-3 mb-4 flex items-start gap-3 shadow-md"
        data-testid="install-app-ios-banner"
      >
        <Share className="w-5 h-5 text-[#E9C46A] flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <p className="font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Install on iPhone
          </p>
          <p className="text-white/80 text-xs leading-relaxed">
            Tap the Share button below, then choose <strong>&ldquo;Add to Home Screen&rdquo;</strong> for one-tap access.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white/60 hover:text-white"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return null;
};

export default InstallAppButton;
